import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheckIcon } from "@phosphor-icons/react";

/**
 * Feb 2026 · Global topbar bell for Authorization Alerts.
 * Polls `/notifications/unread` every 30 s and lazy-loads the last 20
 * items when the dropdown opens. Clicking a row marks it read and
 * navigates to the linked record.
 */
export default function NotificationsBell() {
  const [unread, setUnread] = useState(0);
  const [items,  setItems]  = useState([]);
  const [open,   setOpen]   = useState(false);
  const menuRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread");
      setUnread(Number(data?.count || 0));
    } catch { /* silent */ }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 30000);
    return () => clearInterval(t);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    loadItems();
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, loadItems]);

  const markRead = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); }
    catch { /* silent */ }
    setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    setUnread(u => Math.max(0, u - (n.read ? 0 : 1)));
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); }
    catch { /* silent */ }
    setItems(prev => prev.map(x => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="topbar-auth-bell"
        title="Authorization alerts"
        className="relative p-2 rounded-sm hover:bg-[var(--bg-2)] text-zinc-400 hover:text-amber-300 border border-transparent hover:border-[var(--border)]"
      >
        <ShieldCheckIcon size={16} weight="bold" />
        {unread > 0 && (
          <span
            data-testid="topbar-auth-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-mono font-bold flex items-center justify-center bg-amber-500 text-black"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="topbar-auth-bell-dropdown"
          className="absolute right-0 mt-2 w-96 max-h-[70vh] overflow-y-auto rounded-sm border border-[var(--border)] bg-[var(--bg-1)] shadow-lg z-50"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border)] bg-[var(--bg-2)]/40">
            <div className="overline text-[10px]">Authorization Alerts</div>
            <button onClick={markAll} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-[var(--accent)]" data-testid="topbar-auth-bell-mark-all">
              Mark all read
            </button>
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-xs text-zinc-500 text-center">No notifications yet.</div>
          ) : (
            <ul>
              {items.map(n => (
                <li key={n.id} className={`flex items-start gap-3 border-b border-[var(--border)] px-3 py-2 last:border-b-0 ${!n.read ? "bg-amber-500/5" : ""}`}>
                  <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${n.kind === "AUTH_REQUEST" ? "bg-amber-400" : "bg-emerald-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-white truncate">{n.title}</div>
                    <div className="text-[10px] text-zinc-400 truncate">{n.body}</div>
                    <div className="text-[9px] font-mono text-zinc-600">{(n.created_at || "").slice(0, 19).replace("T", " ")}</div>
                  </div>
                  <Link
                    to={n.link || "#"}
                    onClick={() => { markRead(n); setOpen(false); }}
                    className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline whitespace-nowrap"
                    data-testid={`topbar-auth-bell-open-${n.id}`}
                  >
                    Open →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
